import { useMutation } from 'hooks/useMutation'
import { useQuery } from 'hooks/useQuery'
import { useQueryClient } from 'hooks/useQueryClient'

import {
  addHighFive as addHighFiveQuery,
  isHighFived,
  removeHighFive as removeHighFiveQuery,
} from 'utils/queries/completions'

import { Data } from 'types/query'

type Params = {
  userId?: string
  completionId: string
}

export const useHighFive = ({ completionId, userId }: Params) => {
  const queryClient = useQueryClient()

  const highFiveQuery = useQuery<boolean>(
    ['highFive', userId, completionId],
    () => isHighFived({ userId, completionId }),
    {
      enabled: !!userId,
    },
  )

  /**
   * Adds a highFive for the current user to the HighFives subcollection of a event.
   * Also optimisitically updates the query cache so the UI updates immediately
   * while triggers run in the background to update `highFivedCount` on the event itself.
   */
  const addHighFive = useMutation<unknown, unknown>(
    (data: Data) => {
      return addHighFiveQuery(data)
    },
    {
      retry: 3,
      onMutate: async () => {
        const isCurrentlyHighFived = await queryClient.getQueryData([
          'highFive',
          userId,
          completionId,
        ])
        queryClient.setQueryData(['highFive', userId, completionId], true)
        queryClient.setQueryData(['completion', [completionId]], (data: Data) => {
          if (data) {
            // eslint-disable-next-line no-param-reassign
            data.highFivedCount = data.highFivedCount ? data.highFivedCount + 1 : 1
          }
          return data
        })

        const completion = await queryClient.getQueryData(['completion', completionId])
        return {
          isCurrentlyHighFived,
          completion,
        }
      },
      onError: (_err, _variables, context: any) => {
        queryClient.setQueryData(['highFive', userId, completionId], context.isCurrentlyHighFived)

        queryClient.setQueryData(['completion', [completionId]], (data: Data) => {
          if (data) {
            // eslint-disable-next-line no-param-reassign
            data.highFivedCount = data.highFivedCount ? data.highFivedCount + 1 : 1
          }
          return data
        })
      },
    },
  )

  /**
   * Removes a highFive for the current user to the HighFives subcollection of a event.
   * Also optimisitically updates the query cache so the UI updates immediately
   * while triggers run in the background to update `highFivedCount` on the event itself.
   */
  const removeHighFive = useMutation(
    (data: Data) => {
      return removeHighFiveQuery(data)
    },
    {
      onSuccess: async () => {
        queryClient.setQueryData(['highFive', userId, completionId], false)
        queryClient.setQueryData(['completion', [completionId]], (data: Data) => {
          if (data) {
            // eslint-disable-next-line no-param-reassign
            data.highFivedCount = data.highFivedCount ? data.highFivedCount - 1 : 0
          }
          return data
        })
      },
    },
  )

  return {
    ...highFiveQuery,
    addHighFive,
    removeHighFive,
  }
}
